import { useEffect, useRef, useState } from "react";

interface UseAutoScrollOptions {
	bottomThreshold?: number;
	showButtonThreshold?: number;
	isAutoScrollOnMount?: boolean;
	scrollToBottomOnNewEntries?: boolean;
}

interface UseAutoScrollReturn {
	isAtBottom: boolean;
	scrollToBottom: () => void;
}

export function useAutoScroll(
	containerRef: React.RefObject<HTMLElement | undefined>,
	options?: UseAutoScrollOptions,
): UseAutoScrollReturn {
	const {
		bottomThreshold = 10,
		showButtonThreshold = 100,
		isAutoScrollOnMount = true,
		scrollToBottomOnNewEntries = true,
	} = options ?? {};

	const [isAtBottom, setIsAtBottom] = useState(true);
	const isAutoScrollEnabledRef = useRef(isAutoScrollOnMount);
	const attachedElementRef = useRef<HTMLElement | undefined>(undefined);

	function scrollToBottom() {
		const el = containerRef.current;

		if (!el) return;
		isAutoScrollEnabledRef.current = true;
		el.scrollTo({ left: 0, top: el.scrollHeight, behavior: "smooth" });
	}

	useEffect(() => {
		const element: HTMLElement | undefined = containerRef.current ?? undefined;

		if (element === attachedElementRef.current) return;
		attachedElementRef.current = element;

		if (!element) return;

		const scrollElement = element;
		const entriesContainer = scrollElement.firstElementChild;

		if (isAutoScrollEnabledRef.current) {
			scrollElement.scrollTo({
				left: 0,
				top: scrollElement.scrollHeight,
				behavior: "instant",
			});
		}

		let pendingScrollFrame: number | undefined;

		function snapToBottomOnNextFrame() {
			if (pendingScrollFrame !== undefined) return;

			pendingScrollFrame = requestAnimationFrame(() => {
				pendingScrollFrame = undefined;
				scrollElement.scrollTo({
					left: 0,
					top: scrollElement.scrollHeight,
					behavior: "instant",
				});
			});
		}

		// A new node appended as the last child of the entries container — typically a brand-new top-level entry just landed, rather than streaming text into an existing one.
		function isNewEntryAppended(mutation: MutationRecord) {
			return (
				mutation.type === "childList" &&
				mutation.target === entriesContainer &&
				mutation.addedNodes.length > 0 &&
				mutation.nextSibling === null
			);
		}

		const observer = new MutationObserver((mutations) => {
			if (scrollToBottomOnNewEntries && mutations.some(isNewEntryAppended)) {
				isAutoScrollEnabledRef.current = true;
				snapToBottomOnNextFrame();

				return;
			}

			if (isAutoScrollEnabledRef.current) {
				snapToBottomOnNextFrame();
			}
		});

		observer.observe(element, {
			childList: true,
			subtree: true,
			characterData: true,
		});

		function handleWheel() {
			if (!element) return;

			const distanceFromBottom =
				element.scrollHeight - element.scrollTop - element.clientHeight;

			if (distanceFromBottom > bottomThreshold) {
				isAutoScrollEnabledRef.current = false;
			}
		}

		function handleScroll() {
			if (!element) return;

			const distanceFromBottom =
				element.scrollHeight - element.scrollTop - element.clientHeight;

			if (distanceFromBottom <= bottomThreshold) {
				setIsAtBottom(true);
				isAutoScrollEnabledRef.current = true;
			} else if (
				distanceFromBottom > showButtonThreshold &&
				!isAutoScrollEnabledRef.current
			) {
				setIsAtBottom(false);
			}
		}

		element.addEventListener("wheel", handleWheel, { passive: true });
		element.addEventListener("scroll", handleScroll);

		return () => {
			attachedElementRef.current = undefined;

			if (pendingScrollFrame !== undefined) {
				cancelAnimationFrame(pendingScrollFrame);
			}

			observer.disconnect();
			element.removeEventListener("wheel", handleWheel);
			element.removeEventListener("scroll", handleScroll);
		};
	}, [containerRef.current]);

	return { isAtBottom, scrollToBottom };
}
